/**
 * Width handed to unpic for the `<img src>` fallback of a full-width image.
 *
 * Must appear in `images.sizes` in apps/web/vercel.json; the optimiser rejects
 * a width outside that list just as flatly as a missing one.
 */
const CDN_FALLBACK_WIDTH = 1200;

/**
 * Whether a given image source may be routed through the Vercel image
 * optimiser.
 *
 * `import.meta.env.PROD && import.meta.env.VERCEL` alone is not enough. The
 * optimiser refuses any remote origin that is not listed under `images.
 * remotePatterns` in apps/web/vercel.json — currently just Vercel Blob storage
 * — and answers HTTP 400, which renders as a broken image rather than an
 * unoptimised one. Most covers are uploaded to Blob, but project and article
 * records can be created with an arbitrary `imageUrl` (the MCP tools pass one
 * straight through), so the hint has to be decided per source rather than per
 * build.
 *
 * Keep this in step with `images.remotePatterns` in apps/web/vercel.json.
 */
const ALLOWED_REMOTE_HOST = /(^|\.)public\.blob\.vercel-storage\.com$/;

export function imageCdnProps(
  src?: string | null
): { cdn: "vercel" } | Record<string, never> {
  if (!(import.meta.env.PROD && import.meta.env.VERCEL && src)) {
    return {};
  }

  // Same-origin assets need no allow-list entry; only absolute remote URLs do.
  if (src.startsWith("/")) {
    return { cdn: "vercel" };
  }

  try {
    return ALLOWED_REMOTE_HOST.test(new URL(src).hostname)
      ? { cdn: "vercel" }
      : {};
  } catch {
    // Not a URL we can reason about — leave it as a passthrough.
    return {};
  }
}

/**
 * As `imageCdnProps`, for an `<Image layout="fullWidth">`.
 *
 * unpic derives the `<img src>` fallback from the component's own width, which
 * `fullWidth` leaves undefined. The Vercel optimiser answers HTTP 400 without a
 * `w` parameter, so that fallback ships as a broken image to anything reading
 * `src` instead of `srcset` — crawlers, mainly. Browsers pick a `srcset`
 * candidate and never request it, which is why it took a third-party site audit
 * to surface it at all.
 *
 * Supplying a width is what unpic needs to build a valid fallback, and for
 * `fullWidth` it changes nothing else: the srcset still comes from unpic's own
 * breakpoints, `sizes` and the inline styles are untouched, and the rendered
 * `width`/`height` attributes are dropped either way. unpic's types nonetheless
 * pin `width` to `undefined` on that layout, so the width rides in through this
 * helper's return type rather than as a visible prop — which also keeps the
 * workaround in one place instead of at every call site.
 */
export function imageCdnFullWidthProps(
  src?: string | null
): { cdn: "vercel" } | Record<string, never> {
  const props = imageCdnProps(src);

  if (!("cdn" in props)) {
    return props;
  }

  // Widened deliberately: `width` is what fixes the fallback, and the declared
  // return type has to keep quiet about it for unpic's `fullWidth` props to
  // accept the spread.
  const withFallbackWidth: Record<string, unknown> = {
    ...props,
    width: CDN_FALLBACK_WIDTH,
  };

  return withFallbackWidth as { cdn: "vercel" };
}
